// Grants editor access on a Drive file to a list of email addresses, using the
// Drive API from the browser. The access token comes from the Google Picker
// flow — the app doesn't hold Drive credentials of its own.
//
// One recipient at a time (that is how the Drive API is shaped). Each result
// is {email, name, role, ok, error?}. `sendNotificationEmail` mirrors the
// student's Notify checkbox: no Google-side share email unless they asked for
// one, so this integration honours the same rule as the rest of the app.

const PERMISSIONS_ENDPOINT = (fileId) =>
  `https://www.googleapis.com/drive/v3/files/${encodeURIComponent(fileId)}/permissions`;

async function shareOne({ fileId, accessToken, email, name, role = 'writer', sendNotificationEmail }) {
  const url = `${PERMISSIONS_ENDPOINT(fileId)}?sendNotificationEmail=${sendNotificationEmail ? 'true' : 'false'}${
    sendNotificationEmail
      ? `&emailMessage=${encodeURIComponent(
          `You've been added as an editor on this document from The College Desk. If ${name || 'the student'} shared the essay with you, this is the draft to work in.`
        )}`
      : ''
  }`;

  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ role, type: 'user', emailAddress: email })
    });
    if (res.ok) return { email, name, role, ok: true };

    let detail = '';
    try {
      const body = await res.json();
      detail = body?.error?.message || '';
    } catch {
      detail = await res.text().catch(() => '');
    }
    return { email, name, role, ok: false, error: detail || `Drive responded ${res.status}` };
  } catch (error) {
    return { email, name, role, ok: false, error: error.message || 'Network error' };
  }
}

// Share one file with everyone in `audience`. Returns an array of results in
// the order the audience was given.
export async function shareDocWithAudience({ fileId, accessToken, audience, sendNotificationEmail = false }) {
  if (!fileId) return { results: [], skipped: 'no file id' };
  if (!accessToken) return { results: [], skipped: 'no access token' };
  if (!audience || audience.length === 0) return { results: [], skipped: 'no audience' };

  // Serial rather than parallel: keeps us clear of Drive's per-user rate
  // limits, and a small share list finishes in a second or two anyway.
  const results = [];
  for (const person of audience) {
    if (!person?.email) continue;
    // eslint-disable-next-line no-await-in-loop
    const outcome = await shareOne({
      fileId,
      accessToken,
      email: person.email,
      name: person.name,
      sendNotificationEmail
    });
    results.push(outcome);
  }
  return { results };
}

// A short "shared with X, Y and 1 other; 1 couldn't be added" line for toasts.
export function describeShareResults(results) {
  const good = results.filter((r) => r.ok);
  const bad = results.filter((r) => !r.ok);
  if (good.length === 0 && bad.length === 0) return null;
  const listNames = (list) => {
    const names = list.map((r) => r.name || r.email);
    if (names.length <= 2) return names.join(' and ');
    return `${names.slice(0, 2).join(', ')} and ${names.length - 2} other${names.length - 2 === 1 ? '' : 's'}`;
  };
  const parts = [];
  if (good.length) parts.push(`Shared as editor with ${listNames(good)}`);
  if (bad.length) parts.push(`${bad.length} couldn't be added`);
  return parts.join(' · ');
}
